import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { 
  FlaskConical, Send, CheckCircle, XCircle, Loader2, 
  AlertCircle, Lightbulb, Mail, MailX, Star, Target
} from 'lucide-react';
import './NotificationTest.css';

const NotificationTest = ({ userId }) => {
  const auth = getAuth();
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sendEmail, setSendEmail] = useState(false); // Par défaut: pas d'email

  const handleGenerateNotification = async () => {
    setLoading(true);
    setResult(null);

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Non authentifié');
      }

      console.log('🧪 Génération de notification pour userId:', userId);
      console.log('📧 Envoi d\'email:', sendEmail);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/test-generate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sendEmail: sendEmail
          })
        }
      );

      const data = await response.json();

      // FIX: Handle both success and error cases properly
      if (!response.ok) {
        // Server returned an error status (400, 500, etc.)
        throw new Error(data.message || data.error || `Erreur serveur (${response.status})`);
      }

      if (data.success) {
        setResult({
          type: 'success',
          message: 'Notification générée avec succès !',
          data: data.data
        });
      } else {
        // This handles cases where response.ok is true but success is false
        throw new Error(data.message || data.error || 'Erreur inconnue');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la génération:', error);
      setResult({
        type: 'error',
        message: error.message || 'Erreur lors de la génération',
        error: error
      });
    }

    setLoading(false);
  };

  return (
    <div className="notification-test-v2">
      {/* Header */}
      <div className="test-header">
        <div className="test-header-content">
          <FlaskConical size={32} className="test-icon" />
          <div>
            <h2>Test de Notification Complète</h2>
            <p className="test-subtitle">
              Générez une notification avec GPT pour votre objectif prioritaire
            </p>
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="info-box">
        <Star size={20} />
        <div>
          <strong>Système complet activé</strong>
          <p>
            Ce test génère une notification complète en utilisant GPT pour personnaliser 
            le contenu basé sur votre objectif prioritaire, votre progression et vos notes.
          </p>
        </div>
      </div>

      {/* Explication */}
      <div className="explanation-box">
        <div className="explanation-header">
          <AlertCircle size={20} />
          <h3>Comment ça fonctionne ?</h3>
        </div>
        <ol className="explanation-list">
          <li>
            <strong>Récupération de votre objectif</strong>
            <p>Le système identifie automatiquement votre objectif le plus prioritaire</p>
          </li>
          <li>
            <strong>Analyse de votre progression</strong>
            <p>Examine vos étapes, votre avancement et vos notes récentes</p>
          </li>
          <li>
            <strong>Génération par GPT</strong>
            <p>Crée une phrase d'inspiration et 3 questions personnalisées</p>
          </li>
          <li>
            <strong>Enregistrement</strong>
            <p>Sauvegarde la notification dans votre historique</p>
          </li>
        </ol>
      </div>

      {/* Options */}
      <div className="test-options">
        <h3>Options de test</h3>
        
        <div className="option-card">
          <div className="option-header">
            <div className="option-icon-wrapper">
              {sendEmail ? (
                <Mail size={24} className="option-icon active" />
              ) : (
                <MailX size={24} className="option-icon" />
              )}
            </div>
            <div className="option-info">
              <h4>Envoi d'email</h4>
              <p>
                {sendEmail 
                  ? 'Un email sera envoyé à votre adresse' 
                  : 'Aucun email ne sera envoyé (recommandé pour les tests)'}
              </p>
            </div>
          </div>
          
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              disabled={loading}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Bouton d'action */}
      <button
        onClick={handleGenerateNotification}
        className="btn-generate"
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="spinner" size={20} />
            Génération en cours...
          </>
        ) : (
          <>
            <Star size={20} />
            Générer la notification
          </>
        )}
      </button>

      {/* Résultat */}
      {result && (
        <div className={`result-box ${result.type}`}>
          <div className="result-header">
            {result.type === 'success' ? (
              <CheckCircle size={28} className="result-icon" />
            ) : (
              <XCircle size={28} className="result-icon" />
            )}
            <h3>{result.type === 'success' ? 'Succès !' : 'Aucun résultat'}</h3>
          </div>
          
          <p className="result-message">{result.message}</p>
          
          {result.data && (
            <div className="result-details">
              {/* Utilisateur */}
              <div className="detail-section">
                <h4>👤 Utilisateur</h4>
                <p>{result.data.user.firstName} {result.data.user.lastName}</p>
                <p className="detail-email">{result.data.user.email}</p>
              </div>

              {/* Objectif */}
              <div className="detail-section">
                <h4>🎯 Objectif</h4>
                <p className="detail-objectif">{result.data.objectif.title}</p>
                {result.data.objectif.progress !== undefined && (
                  <>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${result.data.objectif.progress}%` }}
                      ></div>
                    </div>
                    <p className="progress-text">{result.data.objectif.progress}% complété</p>
                  </>
                )}
              </div>

              {/* Contenu généré */}
              <div className="detail-section">
                <h4>💡 Contenu généré par GPT</h4>
                
                <div className="generated-content">
                  {/* Type de notification */}
                  <div className="notification-type-badge">
                    {result.data.notification.type === 'strategy_pre_generated' && '🎯 Stratégie pré-générée'}
                    {result.data.notification.type === 'action_plan_pre_generated' && '⚡ Plan d\'action pré-généré'}
                    {result.data.notification.type === 'inspiration_search_suggested' && '💡 Sujet de recherche'}
                  </div>
                  
                  {/* Preview du contenu selon le type */}
                  {result.data.preview && result.data.preview.contentSummary && (
                    <div className="content-preview">
                      {result.data.preview.contentSummary.type === 'Stratégie pré-générée' && (
                        <div>
                          <p><strong>Sections:</strong> {result.data.preview.contentSummary.sections}</p>
                          <p className="preview-text">{result.data.preview.contentSummary.preview}</p>
                        </div>
                      )}
                      
                      {result.data.preview.contentSummary.type === 'Plan d\'action pré-généré' && (
                        <div>
                          <p><strong>Nombre d'étapes:</strong> {result.data.preview.contentSummary.stepsCount}</p>
                          <p><strong>Temps estimé:</strong> {result.data.preview.contentSummary.estimatedTime}</p>
                          <p className="preview-text">{result.data.preview.contentSummary.preview}</p>
                        </div>
                      )}
                      
                      {result.data.preview.contentSummary.type === 'Sujet de recherche suggéré' && (
                        <div>
                          <p><strong>Question principale:</strong></p>
                          <p className="preview-text">{result.data.preview.contentSummary.mainQuery}</p>
                          <p><strong>Mots-clés:</strong> {result.data.preview.contentSummary.keywordsCount}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Statut email */}
              <div className="detail-section">
                <h4>📧 Email</h4>
                <p className={result.data.emailSent ? 'email-sent' : 'email-not-sent'}>
                  {result.data.emailSent ? (
                    <>
                      <Mail size={16} />
                      Email envoyé avec succès
                    </>
                  ) : (
                    <>
                      <MailX size={16} />
                      Email non envoyé (mode test)
                    </>
                  )}
                </p>
              </div>

              {/* Action */}
              <div className="result-action">
                <Target size={18} />
                <span>La notification a été enregistrée dans votre historique</span>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Guide */}
      <div className="usage-guide">
        <h3>📋 Vérifications après le test</h3>
        <ul className="checklist">
          <li>Allez dans l'onglet "Notifications" pour voir la notification</li>
          <li>Vérifiez que le contenu est personnalisé selon votre objectif</li>
          <li>Testez l'ouverture de la notification (badge "non lu")</li>
          <li>Si email activé : vérifiez votre boîte de réception</li>
          <li>Vérifiez que les statistiques ont été mises à jour</li>
        </ul>
      </div>
    </div>
  );
};

export default NotificationTest;